import React from 'react'

import {
  fetchTrackMetadata,
  initEditedTrack,
} from '@disco/state-management/actions/ServerActions'
import { patchLicenseRequestSubmissionsTrack } from '@disco/state-management/api/license-requests'
import { getTrack } from '@disco/state-management/selectors/tracks'
import {
  getCommonBasicMetadataFields,
  getCommonExtraMetadataFields,
} from '@disco/utils/commonMetadata'

import type { EditableTrack, Track, TrackId } from 'types/track'

import EditMetadataModal from 'components/modals/track/edit-metadata'

import { useDispatch, useSelector, useStore } from 'hooks/use-redux'

type Props = {
  trackId: TrackId | null
  onClose: () => void
}

/**
 * Opens the same track metadata modal used by the explorer's edit-metadata
 * route, driven by local state instead of a URL so the caller's page/URL
 * is preserved once the modal closes.
 */
export const TrackMetadataModal = ({ trackId, onClose }: Props) => {
  const dispatch = useDispatch()
  const store = useStore()
  const [isLoading, setIsLoading] = React.useState(false)
  const [hasFetchedTrack, setHasFetchedTrack] = React.useState(false)
  const track: Track | null | undefined = useSelector(state =>
    trackId ? getTrack(state, { id: trackId }) : null,
  )

  React.useEffect(() => {
    if (!trackId) return

    setHasFetchedTrack(false)
    const loadTrack = async () => {
      setIsLoading(true)
      await dispatch(fetchTrackMetadata(trackId))
      setIsLoading(false)
      setHasFetchedTrack(true)
    }

    loadTrack()
  }, [trackId, dispatch])

  // `saveTrackMetadata` (used by the shared edit-metadata modal below) only
  // writes to the legacy `state.tracks` reducer, so submission list rows
  // rendered from the RTK Query submissions cache never see the edit. Sync
  // it back in here rather than in the shared thunk, since that thunk is
  // used by unrelated features with no notion of this cache.
  React.useEffect(() => {
    if (!track || !hasFetchedTrack) return

    patchLicenseRequestSubmissionsTrack(
      dispatch,
      store.getState,
      track.id,
      draftTrack => {
        Object.assign(draftTrack, {
          name: track.name,
          artist: track.artist ?? '',
          artwork: track.artwork_sizes
            ? {
                artwork: track.artwork ?? track.artwork_sizes.full,
                artwork_sizes: track.artwork_sizes,
              }
            : null,
        })
      },
    )
  }, [track, hasFetchedTrack, dispatch, store])

  React.useEffect(() => {
    if (!track) return

    const editedTrack: EditableTrack = {
      id: track.id,
      url: track.url,
      metadata: {
        ...getCommonBasicMetadataFields([
          {
            artwork: track.artwork_sizes?.full || track.artwork,
            name: track.name,
            album: track.album,
            artist: track.artist,
          },
        ]),
        ...getCommonExtraMetadataFields([track.metadata]),
      },
      comments: track.comments ?? [],
      track_writers: track.track_writers,
      groups: track.groups,
    }
    dispatch(initEditedTrack(editedTrack))
  }, [track, dispatch])

  if (!trackId || !track) {
    return null
  }

  return (
    <EditMetadataModal
      editedTrackRefreshing={isLoading}
      track={track}
      onClose={onClose}
    />
  )
}
